<template>
  <div>
    <v-card class="map-viewer-component-wrapper">
      <div v-if="props.title" class="map-viewer-component-header">
        <div class="header">
          {{ props.title }}
        </div>
        <div class="exit-button">
          <v-btn
            text
            fab
            x-small
            @click="$emit('close-map-viewer')"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
      <v-divider v-if="props.title && props.single" class="mx-4" dark />
      <div v-if="props.title && props.single" class="map-viewer-component-tools">
        <div v-if="props.deviceIdLabel">
          <span class="device-id-label">
            {{ props.deviceIdLabel }}
          </span>
          <span class="device-id-value">
            {{ props.device.shortDeviceId }}
          </span>
        </div>
        <div class="daterangepicker-wrapper">
          <div class="daterangepicker-label">
            {{ $t('map.tracking_records.select_date_range') }}
          </div>
          <DateRangePickerComponent
            :defaultDateRangeValue="props.defaultDateRangeValue"
            @handleDateRange="handleDateRange"
            @query-device-history="$emit('query-device-history')"
          />
        </div>
        <div v-if="props.defaultDateRangeValue.length && vtrObjectForCsv.length" class="actions-wrapper">
          <v-btn
            icon
            color="white"
            @click="refreshData"
          >
            <v-icon>mdi-cached</v-icon>
          </v-btn>
          <downloadCsv
            class="csv-export-wrapper"
            :data="vtrObjectForCsv"
            :name="csvFileName"
          >
            <span>{{ $t('tolling.tracking_records.export_csv') }}</span>
          </downloadCsv>
          <downloadCsv
            class="csv-export-wrapper"
            :data="vtrObjectForCsvBalm"
            :name="csvFileName"
            delimiter=";"
          >
            <span>{{ $t('tolling.tracking_records.export_csv_balm') }}</span>
          </downloadCsv>
        </div>
      </div>
      <div class="map-visualization-wrapper">
        <MapViewerError v-if="props.mapState === 'error'" />
        <MapViewerLoading v-else-if="props.mapState === 'loading'" />
        <MapViewerEmpty v-else-if="props.mapState === 'empty'" />
        <MapViewerEmptyResult v-else-if="props.mapState === 'noResult'" />
        <TrackingRecordsMap
          v-else-if="props.mapState === 'fetched'"
          :vtr="props.deviceRawDataJourney"
          :vtrObject="props.vtrObject"
          :layers="props.layers"
          :width="props.single"
        />
      </div>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import moment, { Moment } from 'moment';
import downloadCsv from 'vue-json-csv';
import DateRangePickerComponent from '@/components/common/DateRangePickerComponent/DateRangePickerComponent.vue';
import TrackingRecordsMap from '@/components/common/TrackingRecordsMap/TrackingRecordsMap.vue';
import './styles/map-viewer-component.scss';
import { PropType } from 'vue/types/v3-component-props';
import { computed } from 'vue';
import { MapViewerEmpty,
  MapViewerEmptyResult,
  MapViewerError,
  MapViewerLoading } from './components/MapViewerState/MapViewerState.vue';

const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  single: {
    type: Boolean,
    default: false
  },
  deviceIdLabel: {
    type: String,
    default: ''
  },
  device: {
    type: Object,
    default: () => ({})
  },
  defaultDateRangeValue: {
    type: Array as PropType<Moment[]>,
    default: [] as Moment[]
  },
  mapState: {
    type: String as PropType<'error' | 'loading' | 'fetched' | 'empty' | 'noResult'>,
    default: ''
  },
  deviceRawDataJourney: {
    type: Array as PropType<number[][]>,
    default: () => ([])
  },
  layers: {
    type: Array as PropType<Array<'raw' | 'mapMatched' | 'default'>>,
    default: () => ([])
  },
  vtrObject: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['handleDateRange', 'query-device-history']);
function handleDateRange(payload: Moment[]) {
  emit('handleDateRange', payload);
}

function refreshData() {
  handleDateRange(props.defaultDateRangeValue);
  emit('query-device-history');
}

const formatHeading = (value: any) => {
  const rounded = Number(value).toFixed(1);
  const tenth = Math.round(parseFloat(rounded) * 10);
  return tenth.toString().padStart(4, '0');
};

const formatHdop = (value: any) => {
  const rounded = Number(value).toFixed(1);
  const intVal = Math.round(parseFloat(rounded) * 10);
  return intVal.toString().padStart(3, '0');
};

const vtrObjectForCsv = computed(() => (props.vtrObject
      && props.vtrObject.features
  ? props.vtrObject.features.map((feature: any) => ({
    'datetime (UTC)': moment.unix(feature.properties.timestamp).utc().format('DD/MM/YYYY HH:mm:ss'),
    'latitude (°)': feature.geometry.coordinates[1],
    'longitude (°)': feature.geometry.coordinates[0],
    'cellID': feature.properties.cellID,
    'elevation (m)': feature.properties.elevation,
    'HDOP': feature.properties.hdop,
    'heading (°)': feature.properties.heading,
    'speed (km/h)': feature.properties.speed,
    'obe status': feature.properties.obeStatus,
    'exemption': feature.properties.exemption,
    'private mode': feature.properties.privateMode,
    'tolling state': feature.properties.tollingState,
    'Position Counter': feature.properties.counter
  })) : []));

const vtrObjectForCsvBalm = computed(() => (
  props.vtrObject?.features?.map((feature: any) => ([
    moment.unix(feature.properties.timestamp).utc().format('YYYYMMDDHHmmss[Z]'),
    Math.trunc(feature.geometry.coordinates[1] * 1e6),
    Math.trunc(feature.geometry.coordinates[0] * 1e6),
    formatHeading(feature.properties.heading),
    Math.round((feature.properties.speed * 100000) / 3600).toString().padStart(4, '0'),
    feature.properties.usedSatellites?.toString().padStart(2, '0'),
    formatHdop(feature.properties.hdop),
    0,
    0
  ])) ?? []
));

const csvFileName = computed(() => (props.device.shortDeviceId && props.defaultDateRangeValue.length === 2
  ? `${props.device.shortDeviceId}-${moment(props.defaultDateRangeValue[0]).utc()}-${moment(props.defaultDateRangeValue[1]).utc()}.csv`
  : 'corrupted-file'));

</script>
